import { useState } from "react";
import { useV1ProductionBomFamiliesCreate, useV1InventoryMaterialRevisionsList } from "@/api/generated/v1/v1";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { PageBanner } from "@/shared/components/PageBanner";

export function BomFamilyCreatePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutateAsync: createBomFamily, isPending } = useV1ProductionBomFamiliesCreate();
  const { data: materialRevisionsData, isLoading: isLoadingMaterials } = useV1InventoryMaterialRevisionsList({ status: "published", material_type__in: "semi_finished,finished_product" } as any);

  const [formData, setFormData] = useState({
    code: "",
    name: "",
    description: "",
    output_material_revision: "", // UUID
  });
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg("");
    
    try {
      const payload: any = {
        code: formData.code,
        name: formData.name,
      };
      
      if (formData.description) payload.description = formData.description;
      if (formData.output_material_revision) payload.output_material_revision = formData.output_material_revision;

      const res = await createBomFamily({ data: payload });
      queryClient.invalidateQueries({ queryKey: ["v1ProductionBomFamiliesList"] });
      // Navigate to detail page
      navigate(`/bom/families/${(res as any).id}`);
    } catch (err: any) {
      setErrorMsg(
        err.response?.data?.detail || 
        JSON.stringify(err.response?.data) || 
        "خطا در ایجاد خانواده BOM"
      );
    }
  };

  return (
    <div
      dir="rtl"
      style={{
        padding: "0.5rem 0",
        maxWidth: "100%",
        margin: "0",
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
        fontFamily: "var(--font-family-fa, sans-serif)",
      }}
    >
      <style>{`
        .form-input-control {
          width: 100%;
          padding: 0.75rem 1rem;
          border-radius: 12px;
          border: 1px solid var(--color-border, #E4DCC8);
          background: #FAFAFA;
          color: var(--color-text-main, #2B2B2B);
          font-size: 0.95rem;
          font-weight: 600;
          outline: none;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .form-input-control:hover {
          background: #FFFFFF;
          border-color: rgba(0, 114, 112, 0.4);
        }
        .form-input-control:focus {
          background: #FFFFFF;
          border-color: #007270;
          box-shadow: 0 0 0 4px rgba(0, 114, 112, 0.15);
        }
      `}</style>

      <PageBanner
        title="ایجاد خانواده BOM"
        subtitle="تعریف و شناسایی یک گروه محصولی جدید برای تخصیص نسخه‌های ساخت"
        icon="✨"
        helpLink="/help/bom/families/create"
      >
        <button
          type="button"
          onClick={() => navigate("/bom/families")}
          style={{
            padding: "0.6rem 1.1rem",
            borderRadius: "12px",
            background: "rgba(255, 255, 255, 0.15)",
            color: "#ffffff",
            border: "1px solid rgba(255, 255, 255, 0.3)",
            fontWeight: 700,
            fontSize: "0.9rem",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "0.4rem",
            transition: "all 0.2s ease",
          }}
        >
          <span>بازگشت به لیست</span>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 18l6-6-6-6"/>
          </svg>
        </button>
      </PageBanner>

      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
        
        {errorMsg && (
          <div
            role="alert"
            style={{
              padding: "1rem 1.25rem",
              borderRadius: "12px",
              background: "rgba(239, 68, 68, 0.08)",
              border: "1px solid rgba(239, 68, 68, 0.3)",
              color: "#B91C1C",
              fontSize: "0.9rem",
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
            }}
          >
            <span style={{ fontSize: "1.2rem" }}>🚨</span>
            <span>{errorMsg}</span>
          </div>
        )}

        {/* CARD 1: BASIC INFORMATION */}
        <div className="brand-card-extand" style={{ padding: "1.5rem", overflow: "visible" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.5rem", borderBottom: "1px solid var(--color-border, #E4DCC8)", paddingBottom: "1rem" }}>
            <span style={{ fontSize: "1.4rem", background: "rgba(0,114,112,0.08)", width: "36px", height: "36px", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center" }}>📋</span>
            <div>
              <h2 style={{ fontSize: "1.1rem", fontWeight: "800", color: "#007270", margin: 0 }}>
                اطلاعات پایه خانواده
              </h2>
              <span style={{ fontSize: "0.85rem", color: "var(--color-text-muted, #666)" }}>
                کد یکتا و عنوان اصلی محصول را تعیین فرمایید.
              </span>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1.5rem" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              <label style={{ fontSize: "0.9rem", fontWeight: 700, color: "var(--color-text-main, #2B2B2B)" }}>
                کد خانواده <span style={{ color: "#EF4444" }}>*</span>
              </label>
              <input
                type="text"
                required
                className="form-input-control"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                placeholder="مثال: BOM-001"
                dir="ltr"
                style={{ textAlign: "left", fontFamily: "monospace" }}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              <label style={{ fontSize: "0.9rem", fontWeight: 700, color: "var(--color-text-main, #2B2B2B)" }}>
                نام خانواده <span style={{ color: "#EF4444" }}>*</span>
              </label>
              <input
                type="text"
                required
                className="form-input-control"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="مثال: قطعات محصول آلفا"
              />
            </div>
          </div>
          
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginTop: "1.5rem" }}>
            <label style={{ fontSize: "0.9rem", fontWeight: 700, color: "var(--color-text-main, #2B2B2B)", display: "flex", justifyContent: "space-between" }}>
              <span>توضیحات</span>
              <span style={{ fontSize: "0.8rem", color: "#888", fontWeight: 500 }}>(اختیاری)</span>
            </label>
            <textarea
              className="form-input-control"
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="توضیحات مربوط به این خانواده ساختار محصول..."
              style={{ resize: "vertical", minHeight: "100px" }}
            />
          </div>
        </div>

        {/* CARD 2: OUTPUT MATERIAL */}
        <div className="brand-card-extand" style={{ padding: "1.5rem", overflow: "visible" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.5rem", borderBottom: "1px solid var(--color-border, #E4DCC8)", paddingBottom: "1rem" }}>
            <span style={{ fontSize: "1.4rem", background: "rgba(212,175,55,0.12)", width: "36px", height: "36px", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center" }}>📦</span>
            <div>
              <h2 style={{ fontSize: "1.1rem", fontWeight: "800", color: "#007270", margin: 0 }}>
                متریال خروجی
              </h2>
              <span style={{ fontSize: "0.85rem", color: "var(--color-text-muted, #666)" }}>
                نسخه متریال خروجی این خانواده ساخت را انتخاب کنید.
              </span>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <label style={{ fontSize: "0.9rem", fontWeight: 700, color: "var(--color-text-main, #2B2B2B)" }}>
              متریال خروجی <span style={{ color: "#EF4444" }}>*</span>
            </label>
            <select
              required
              className="form-input-control"
              value={formData.output_material_revision}
              onChange={(e) => setFormData({ ...formData, output_material_revision: e.target.value })}
              dir="rtl"
              style={{ cursor: "pointer" }}
            >
              <option value="">-- انتخاب متریال خروجی --</option>
              {isLoadingMaterials ? (
                <option value="" disabled>در حال دریافت لیست متریال‌ها...</option>
              ) : (
                materialRevisionsData?.results?.map((mat: any) => (
                  <option key={mat.id} value={mat.id}>
                    {mat.sku || mat.code} - {mat.name} (نسخه {mat.revision_no})
                  </option>
                ))
              )}
            </select>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "0.5rem" }}>
              <span style={{ color: "#3B82F6", fontSize: "0.9rem" }}>ℹ️</span>
              <p style={{ margin: 0, fontSize: "0.8rem", color: "var(--color-text-muted)", fontWeight: 600 }}>
                فقط متریال‌های منتشر شده (تایید نهایی) در این لیست نمایش داده می‌شوند.
              </p>
            </div>
          </div>
        </div>

        {/* ACTION BAR */}
        <div 
          className="brand-card-extand" 
          style={{ 
            padding: "1.25rem 1.5rem", 
            display: "flex", 
            justifyContent: "flex-end", 
            alignItems: "center", 
            gap: "1rem",
            background: "rgba(255,255,255,0.5)",
            backdropFilter: "blur(10px)",
            position: "sticky",
            bottom: "1rem",
            zIndex: 10,
            boxShadow: "0 -4px 20px rgba(0,0,0,0.03)"
          }}
        >
          <button
            type="button"
            onClick={() => navigate("/bom/families")}
            style={{
              padding: "0.75rem 1.5rem",
              borderRadius: "12px",
              background: "transparent",
              color: "#666",
              border: "1px solid var(--color-border, #E4DCC8)",
              fontWeight: 700,
              fontSize: "0.95rem",
              cursor: "pointer",
              transition: "all 0.2s ease"
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = "#FFFFFF";
              e.currentTarget.style.color = "#2B2B2B";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = "#666";
            }}
          >
            انصراف
          </button>
          
          <button
            type="submit"
            disabled={isPending}
            style={{
              padding: "0.75rem 2.25rem",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #007270 0%, #005a58 100%)",
              color: "#ffffff",
              border: "none",
              fontWeight: 800,
              fontSize: "1rem",
              cursor: isPending ? "not-allowed" : "pointer",
              boxShadow: "0 8px 16px rgba(0, 114, 112, 0.25)",
              opacity: isPending ? 0.8 : 1,
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem"
            }}
            onMouseOver={(e) => {
              if (!isPending) e.currentTarget.style.transform = "translateY(-2px)";
            }}
            onMouseOut={(e) => {
              if (!isPending) e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            {isPending ? (
              <>
                <span>⏳</span>
                <span>در حال پردازش...</span>
              </>
            ) : (
              <>
                <span>ایجاد خانواده BOM</span>
                <span>✓</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
